import type { DiscoveryGetPermissionForUserResponse } from "@amzn/ads-discovery-theme-service-typescript-client";
import { useAsync, useMountEffect, useUpdateEffect } from "@react-hookz/web";
import React from "react";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { useStore } from "zustand";

import { getDiscoveryApiClient } from "../../api/discoveryApi/discoveryApi";
import { InsightLinkageModal } from "../../components/createInsightLinkage/InsightLinkageModal";
import { SfContext, ErrorBoundary } from "../../components/discovery";
import { InsightDetailSheet } from "../../components/discovery/insights/insightDetails/InsightDetailSheet";
import { InsightSelectionSheet } from "../../components/discovery/insights/insightDetails/InsightSelectionSheet";
import { TopNavBar } from "../../components/discovery/topNavBar/TopNavBar";
import { useSfObjectContextPitchUploadEffect } from "../../hooks/misc/useSfObjectContextPitchUploadEffect";
import { useExploreWithAINotification } from "../../hooks/notifications/useExploreWithAINotification";
import { authStore } from "../../store/auth";
import { navigationStore } from "../../store/navigation";
import { yourInsightFilterStore } from "../../store/yourInsightsFilter";
import {
    PLM_ALLOWLISTED_ROLES,
    YourInsightFilterContentType,
} from "../../types/filters/yourInsights";
import { TopNavBarTabs } from "../../types/navigation";
import {
    IHubQueryParam,
    isAccountAgnosticId,
    QueryParam,
} from "../../utils/permission.utils";
import {
    initialTab,
    getTabQueryParam,
    getYourInsightsContentType,
    getYourInsightsSubType,
    getYourInsightsContentTypeQueryParam,
    getYourInsightsSubTypeQueryParam,
} from "../../utils/tab.utils";
import { getValueFromSearchParams } from "../../utils/window.utils";

import { AccountAgnosticExploreWithAI } from "./AccountAgnosticExploreWithAI";
import { AllInsights } from "./AllInsights";
import { ExploreWithAI } from "./ExploreWithAI";
import { YourInsights } from "./YourInsights";

const StyledDiscoverContainer = styled.div`
    padding: 24px;
`;

const TabContent: React.FunctionComponent<{
    activeTab: TopNavBarTabs;
    hasPlmAccess: boolean;
}> = ({ activeTab, hasPlmAccess }) => {
    switch (activeTab) {
        case TopNavBarTabs.ALL_INSIGHTS:
            return <AllInsights />;
        case TopNavBarTabs.YOUR_INSIGHT_DECKS:
            return <YourInsights hasPlmAccess={hasPlmAccess} />;
        case TopNavBarTabs.EXPLORE_WITH_AI:
            return isAccountAgnosticId() ? (
                <AccountAgnosticExploreWithAI />
            ) : (
                <ExploreWithAI />
            );
    }
};

export const Discovery: React.FunctionComponent = () => {
    const [activeTab, setActiveTab, identifier, setIdentifier] = useStore(
        navigationStore,
        (state) => [
            state.activeTab,
            state.setActiveTab,
            state.identifier,
            state.setIdentifier,
        ],
    );

    const [searchParams, setSearchParams] = useSearchParams();

    const [userAccessRoles, setUserAccessRoles] = useStore(
        authStore,
        (state) => [state.accessRoles, state.setAccessRoles],
    );

    const [contentType, contentSubType, setYourInsightsTypes] = useStore(
        yourInsightFilterStore,
        (state) => [state.contentType, state.contentSubType, state.setTypes],
    );

    useUpdateEffect(() => {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set(
            IHubQueryParam.INITIAL_TAB,
            getTabQueryParam(activeTab),
        );

        if (identifier) {
            newSearchParams.set(QueryParam.IDENTIFIER, identifier);
        } else {
            newSearchParams.delete(QueryParam.IDENTIFIER);
        }

        if (activeTab === TopNavBarTabs.YOUR_INSIGHT_DECKS) {
            newSearchParams.set(
                IHubQueryParam.YOUR_INSIGHT_CONTENT_TYPE,
                getYourInsightsContentTypeQueryParam(contentType),
            );
            if (contentSubType) {
                newSearchParams.set(
                    IHubQueryParam.YOUR_INSIGHT_SUB_TYPE,
                    getYourInsightsSubTypeQueryParam(contentSubType),
                );
            } else {
                newSearchParams.delete(IHubQueryParam.YOUR_INSIGHT_SUB_TYPE);
            }
        } else {
            newSearchParams.delete(IHubQueryParam.YOUR_INSIGHT_CONTENT_TYPE);
            newSearchParams.delete(IHubQueryParam.YOUR_INSIGHT_SUB_TYPE);
        }

        setSearchParams(newSearchParams);
    }, [activeTab, contentType, contentSubType, identifier]);

    const [_, userRoleAction] = useAsync(async () => {
        const data: DiscoveryGetPermissionForUserResponse = await (
            await getDiscoveryApiClient()
        ).getPermissionForUser();
        const userRoles: string[] = data.userRole ?? [];
        const hasPlmAccess: boolean = userRoles.some((role) =>
            PLM_ALLOWLISTED_ROLES.includes(role),
        );
        setUserAccessRoles({ ...userAccessRoles, plm: hasPlmAccess });
    });

    useMountEffect(() => {
        void userRoleAction.execute();
        setActiveTab(initialTab());
        setIdentifier(getValueFromSearchParams(QueryParam.IDENTIFIER));

        // Initialize Your Insights state from URL if present
        const urlContentType = getYourInsightsContentType();
        const urlSubType = getYourInsightsSubType();
        if (
            urlContentType !== YourInsightFilterContentType.INSIGHTS ||
            urlSubType
        ) {
            setYourInsightsTypes(urlContentType, urlSubType);
        }
    });

    const hasPlmAccess = userAccessRoles.plm;

    useSfObjectContextPitchUploadEffect();
    useExploreWithAINotification();

    return (
        <ErrorBoundary>
            <StyledDiscoverContainer>
                <TopNavBar hasPlmAccess={hasPlmAccess} />
                <SfContext />
                <TabContent activeTab={activeTab} hasPlmAccess={hasPlmAccess} />
            </StyledDiscoverContainer>
            <InsightSelectionSheet />
            <InsightDetailSheet />
            <InsightLinkageModal />
        </ErrorBoundary>
    );
};
